import { extend, configure } from 'vee-validate';
import {
  required,
  required_if as requiredIf,
  is,
  email,
  regex,
  min,
  min_value as minValue,
  max,
  numeric,
  // eslint-disable-next-line
  alpha_num,
} from 'vee-validate/dist/rules';
import en from 'vee-validate/dist/locale/en.json';
import moment from 'moment';
import api from '@/api';
import store from '@/store/store';

// Allow for reuse with multiple messages
const maxDecimalRule = {
  validate: (value, { places }) => {
    if (Number.isNaN(Number(value))) return false;
    const stringValue = value.toString();
    if (stringValue.indexOf('.') !== -1) return stringValue.split('.')[1].length <= Number(places);
    return true;
  },
  params: [{ name: 'places' }],
};

const configureValidation = () => {
  extend('required', {
    ...required,
    message: '{_field_} is required.',
  });
  extend('roleRequired', {
    ...required,
    message: 'Role is required. Only a Landowner or Designated Agent can create a FON.',
  });
  extend('certMethodRequired', {
    ...required,
    message: 'Certification method is required for this FON.',
  });
  extend('required_if', {
    ...requiredIf,
    message: en.messages.required_if,
  });
  extend('multiRequired', {
    ...required,
    message: 'At least one {_field_} is required.',
  });
  extend('is', {
    ...is,
    message: en.messages.is,
  });
  extend('email', {
    ...email,
    message: en.messages.email,
  });
  extend('regex', {
    ...regex,
    message: en.messages.regex,
  });
  extend('min', {
    ...min,
    message: en.messages.min,
  });
  extend('min_value', {
    ...minValue,
    message: en.messages.min_value,
  });
  extend('max', {
    ...max,
    message: en.messages.max,
  });
  extend('numeric', {
    ...numeric,
    message: 'The {_field_} field must be a whole number.',
  });
  extend('alpha_num', {
    // eslint-disable-next-line
    ...alpha_num,
    message: 'The {_field_} field must not contain any special characters.',
  });
  // Vee validate requires this rule to be registered per message in this case,
  // since it doesn't allow interpolating the message.
  extend('max_decimal_singular', {
    ...maxDecimalRule,
    message: 'The {_field_} field must be numeric and may contain only one decimal place.',
  });
  extend('max_decimal', {
    ...maxDecimalRule,
    message: 'The {_field_} field must be numeric and may up to {places} decimal places.',
  });
  extend('money', {
    message: 'The {_field_} field must be dollars and cents format.',
    validate: value => /^[0-9]+\.?[0-9]?[0-9]?$/.test(value),
  });
  extend('phoneRegex', {
    ...regex,
    message: 'Numbers only with area code (10 digit number).',
  });
  extend('licenseRegex', {
    ...regex,
    message: 'The Licensed Forester Number field format is invalid. Be sure to include "LF" or "FI" at the beginning of your license number and a minimum of 3 characters.',
  });
  extend('maineGovEmailRegex', {
    ...regex,
    message: 'The email must be a maine.gov email address.',
  });
  extend('passwordMin', {
    ...min,
    message: 'Password requires at least {length} characters.',
  });
  extend('passwordNonAlpha', {
    message: 'Password requires 1 of the following special characters (!@*_).',
    validate: value => /[!@*_]/.test(value),
  });
  extend('passwordNumber', {
    message: 'Password requires 1 number.',
    validate: value => /\d/.test(value),
  });
  extend('passwordUppercase', {
    message: 'Password requires 1 uppercase letter.',
    validate: value => /[A-Z]/.test(value),
  });
  extend('passwordLowercase', {
    message: 'Password requires 1 lowercase letter.',
    validate: value => /[a-z]/.test(value),
  });
  extend('passwordNoSpaces', {
    message: 'Password cannot contain spaces.',
    validate: value => !/[ ]/.test(value),
  });
  extend('notSimilar', {
    message: 'Password cannot contain part of entered email.',
    validate: (value, { other }) => {
      if (!other) return false;
      const normalValue = value.toUpperCase();
      const normalOther = other.toUpperCase();
      const chars = normalOther.split('').slice(0, normalOther.length - 2);
      const tokens = chars.map((char, index) => normalOther.substr(index, 3));
      return !tokens.some(token => normalValue.includes(token));
    },
    params: [{ name: 'other', isTarget: true }],
  });
  extend('yearsAfter', {
    validate: (value, { other, years }) => {
      if (!other || !value) return true;
      const otherDate = new Date(other);
      return otherDate.setFullYear(otherDate.getFullYear() + Number(years)) >= new Date(value);
    },
    params: [{ name: 'other', isTarget: true }, { name: 'years' }],
    message: '{_field_} must be at most {years} years after {other}.',
  });
  extend('before', {
    validate: (value, { other }) => {
      if (!other || !value) return true;
      return new Date(value) <= new Date(other);
    },
    params: [{ name: 'other', isTarget: true }],
    message: '{_field_} must be before {other}.',
  });
  extend('after', {
    validate: (value, { other }) => {
      if (!other || !value) return true;
      return new Date(value) >= new Date(other);
    },
    params: [{ name: 'other', isTarget: true }],
    message: '{_field_} must be after {other}.',
  });
  extend('afterStartDate', {
    validate: (value, { operationStartDate }) => {
      const momentValue = moment.utc(value).startOf('day');
      const momentStartDate = moment.utc(operationStartDate).startOf('day');
      return momentValue.isSameOrAfter(momentStartDate);
    },
    params: [{ name: 'operationStartDate', isTarget: false }],
    message: '{_field_} must be on or after the Operation Start Date indicated on the Overview page.',
  });

  const lessThanOrEqual = (a, b) => {
    if (!a || !b) return true;
    const numberA = Number(a);
    const numberB = Number(b);
    if (Number.isNaN(numberA) || Number.isNaN(numberB)) return true;
    return numberA <= numberB;
  };

  extend('lessThanOrEqual', {
    validate: (value, { other }) => lessThanOrEqual(value, other),
    params: [{ name: 'other', isTarget: true }],
    message: '{_field_} must be less than or equal to {other}.',  //_other_ is the value of the target field, while {other} is the name of the target field.
  });

  extend('lessThanOrEqualPropertyAcreage', {
    validate: (value, { propertyAcreage }) => lessThanOrEqual(value, propertyAcreage),
    params: [{ name: 'propertyAcreage', isTarget: false }],
    message: '{_field_} must be less than or equal to Property Parcel Acreage ({propertyAcreage} Acres) indicated on the Overview page.',
  });

  extend('greaterThanOrEqual', {
    validate: (value, { other }) => {
      if (!other || !value) return true;
      const numberValue = Number(value);
      const numberOther = Number(other);
      if (Number.isNaN(numberOther) || Number.isNaN(numberValue)) return true;
      return numberValue >= numberOther;
    },
    params: [{ name: 'other', isTarget: true }],
    message: '{_field_} must be greater than or equal to {other}.',
  });

  extend('notification_exists', {
    message: 'Notification {_value_} not found.',
    validate: value => new Promise((resolve) => {
      api.get(`/notification/${value}/getId`)
        .then(response => resolve(response.data));
    }),
  },
  {
    immediate: false,
  });
  extend('checkCertificationMethod', {
    validate: async (value) => {
      const error = await store.dispatch('Notification/checkCertificationMethod', value);
      // on new FONs the error comes back as an empty object. Need more than a truthy check
      return error && typeof error === 'string' ? { valid: false, data: { serverMessage: error } } : true;
    },
    message: '{serverMessage}',
  });
  configure({
    classes: {
      valid: 'is-valid',
      invalid: 'is-invalid',
    },
  });
};

export default configureValidation;
